// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Sprint 63.22.0 (decision 3) — one owner for the bottom-right corner.
 *
 * Sprint 61.0.10.4 AC11 gave the UI Assist toolbar the job of raising the toast
 * stack while it was mounted, by writing `--dm-toast-clearance` on the document
 * root. That was right for one occupant. With the Forseti launcher in the same
 * corner there are two, and two components each writing and removing the same
 * variable is a race: whichever unmounts first removes the other's clearance,
 * and two `position: fixed; bottom: 16px; right: 16px` elements land on each
 * other.
 *
 * So the corner is a dock:
 *   - `BottomRightDockProvider` holds the registered slots and is the ONLY
 *     writer of `--dm-toast-clearance`;
 *   - `<BottomRightDock />` renders the ONE fixed flex row, wherever the layout
 *     places it (`AdminLayout` puts it inside `<main>`, so the star belongs to
 *     the main landmark rather than floating outside every landmark);
 *   - an occupant never positions itself: it wraps its content in
 *     `<BottomRightDockSlot>` (or asks `useBottomRightDockSlot` for its slot
 *     element) and the row lays the slots out side by side. Two occupants that
 *     flow in one row cannot overlap at any width, and the toast stack starts
 *     above the row whatever is in it.
 *
 * Occupants today: the Forseti launcher (order 100, at the edge) and the UI
 * Assist toolbar (order 10, to its left — 63.22.0 stream D moved it in and
 * removed `UIAssistProvider`'s own write of the clearance).
 *
 * Registered in REUSE.md ("Bottom-right dock").
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

/**
 * Toast clearance when the dock is occupied and cannot be measured: the dock's
 * 16px offset, the tallest occupant (the 44px launcher target), and an 8px gap
 * — 68px. Where `ResizeObserver` exists the measured row height replaces 44px.
 */
export const DOCK_OFFSET_PX = 16;
export const DOCK_GAP_PX = 8;
export const DOCK_FALLBACK_CLEARANCE = '4.25rem';

interface SlotEntry {
  id: string;
  order: number;
}

interface DockContextValue {
  register: (id: string, order: number) => void;
  unregister: (id: string) => void;
  slotElement: (id: string) => HTMLElement | null;
}

interface DockRenderContextValue {
  slots: SlotEntry[];
  slotRef: (id: string) => (el: HTMLElement | null) => void;
  setMeasured: (height: number | null) => void;
}

const DockContext = createContext<DockContextValue | null>(null);
const DockRenderContext = createContext<DockRenderContextValue | null>(null);

export function BottomRightDockProvider({ children }: { children: ReactNode }): JSX.Element {
  const [slots, setSlots] = useState<SlotEntry[]>([]);
  const [elements, setElements] = useState<Record<string, HTMLElement | null>>({});
  const [measured, setMeasured] = useState<number | null>(null);
  const occupied = slots.length > 0;

  const register = useCallback((id: string, order: number) => {
    setSlots((prev) => {
      const existing = prev.find((s) => s.id === id);
      if (existing && existing.order === order) return prev;
      return [...prev.filter((s) => s.id !== id), { id, order }];
    });
  }, []);

  const unregister = useCallback((id: string) => {
    setSlots((prev) => (prev.some((s) => s.id === id) ? prev.filter((s) => s.id !== id) : prev));
  }, []);

  const slotElement = useCallback((id: string) => elements[id] ?? null, [elements]);

  /**
   * One ref callback per slot id, kept for the provider's lifetime. A fresh
   * arrow per render would be detached (called with null) and re-attached on
   * every render, and each call sets state — an update loop.
   */
  const refCallbacks = useRef(new Map<string, (el: HTMLElement | null) => void>());
  const slotRef = useCallback((id: string) => {
    let cb = refCallbacks.current.get(id);
    if (!cb) {
      cb = (el: HTMLElement | null) => {
        setElements((prev) => (prev[id] === el ? prev : { ...prev, [id]: el }));
      };
      refCallbacks.current.set(id, cb);
    }
    return cb;
  }, []);

  const clearance =
    measured !== null
      ? `${DOCK_OFFSET_PX + Math.ceil(measured) + DOCK_GAP_PX}px`
      : DOCK_FALLBACK_CLEARANCE;

  // The single writer of the two clearances. Released when the corner empties
  // and when the provider unmounts, so a stack never floats above nothing.
  //   - `--dm-toast-clearance` raises the toast stack above the row
  //     (toast.css);
  //   - `--dm-dock-clearance` pads the bottom of the content pane
  //     (`.admin-content`, admin.css). The pane is the scroll container, and a
  //     sticky footer's stuck position is measured inside its padding, so a
  //     `position: sticky; bottom: …` bar (`.ff-nav-actions`, `.dm-bulk-bar`)
  //     and the last row of any page stop above the row instead of under the
  //     star (gate E12.5 pass 1, P2-8).
  useEffect(() => {
    const root = document.documentElement;
    if (occupied) {
      root.style.setProperty('--dm-toast-clearance', clearance);
      root.style.setProperty('--dm-dock-clearance', clearance);
    } else {
      root.style.removeProperty('--dm-toast-clearance');
      root.style.removeProperty('--dm-dock-clearance');
    }
    return () => {
      root.style.removeProperty('--dm-toast-clearance');
      root.style.removeProperty('--dm-dock-clearance');
    };
  }, [occupied, clearance]);

  const value = useMemo<DockContextValue>(
    () => ({ register, unregister, slotElement }),
    [register, unregister, slotElement],
  );
  const renderValue = useMemo<DockRenderContextValue>(
    () => ({ slots, slotRef, setMeasured }),
    [slots, slotRef],
  );

  return (
    <DockContext.Provider value={value}>
      <DockRenderContext.Provider value={renderValue}>{children}</DockRenderContext.Provider>
    </DockContext.Provider>
  );
}

/**
 * The one fixed row in the corner. Render it once, inside the provider.
 *
 * `hidden` takes the row off the page without unregistering its occupants —
 * `AdminLayout` passes it while the phone-width navigation drawer is open, so
 * the star never sits on top of the drawer or its backdrop and is not a tab
 * stop behind the drawer's focus trap (gate E12.5 pass 1, P2-11).
 */
export function BottomRightDock({ hidden = false }: { hidden?: boolean } = {}): JSX.Element | null {
  const dock = useContext(DockRenderContext);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const slots = dock?.slots ?? [];
  const occupied = slots.length > 0;
  const setMeasured = dock?.setMeasured;

  // Measure the row when the platform can; otherwise the fallback applies.
  useEffect(() => {
    const node = rowRef.current;
    if (!setMeasured) return undefined;
    if (!occupied || !node || typeof ResizeObserver === 'undefined') {
      setMeasured(null);
      return undefined;
    }
    const observer = new ResizeObserver(() => {
      const height = node.getBoundingClientRect().height;
      setMeasured(height > 0 ? height : null);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [occupied, setMeasured]);

  if (!dock) return null;
  const ordered = [...slots].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));

  return (
    <div
      ref={rowRef}
      className="dm-bottom-right-dock"
      data-testid="bottom-right-dock"
      data-occupied={occupied ? 'true' : 'false'}
      hidden={hidden}
    >
      {ordered.map((slot) => (
        <div
          key={slot.id}
          className="dm-bottom-right-dock__slot"
          data-dock-slot={slot.id}
          ref={dock.slotRef(slot.id)}
        />
      ))}
    </div>
  );
}

/**
 * Claim a slot in the dock. Returns the slot's element once the dock has
 * rendered it (portal your content into it), or `null` while it has not — and
 * always `null` outside a provider, so an occupant rendered alone renders
 * nothing rather than a second fixed element in the corner.
 *
 * `order` sorts left to right: the smallest order is furthest from the edge.
 */
export function useBottomRightDockSlot(id: string, order = 0): HTMLElement | null {
  const dock = useContext(DockContext);
  // Registration depends on the two stable callbacks only. The context value
  // itself changes whenever a slot element mounts, and re-running this effect
  // on that change would unregister and re-register the slot in a loop.
  const register = dock?.register;
  const unregister = dock?.unregister;
  useEffect(() => {
    if (!register || !unregister) return undefined;
    register(id, order);
    return () => unregister(id);
  }, [register, unregister, id, order]);
  return dock ? dock.slotElement(id) : null;
}

/** Render children into a dock slot. */
export function BottomRightDockSlot({
  id,
  order = 0,
  children,
}: {
  id: string;
  order?: number;
  children: ReactNode;
}): JSX.Element | null {
  const element = useBottomRightDockSlot(id, order);
  return element ? createPortal(children, element) : null;
}
